import {
  type GraphemeLessonPart,
  isGraphemeReadingIntroducedAtGrade,
  type Level,
  orderIntroducedPinyinForGrapheme,
} from "@-/dev.improve.haniverse.lib.level";
import { useActorUser } from "@-/dev.improve.haniverse.lib.user";
import type { GraphemeMorphemeMnemonicsPanelMorphemeSlice } from "@-/dev.improve.haniverse.lib.morpheme.entities";
import type { ResolvedQuery } from "@-/dev.improve.haniverse.lib.query-options";
import { usePrevalentPinyin } from "@-/dev.improve.haniverse.routes.nav.app._lib.morpheme.grapheme.pinyin.prevalence";
import type { MorphemePanelsMnemonicsNavigationProps } from "@-/dev.improve.haniverse.routes.nav.app._components.morpheme.panels";
import { useLearnMnemonicsProgrammaticAudio } from "@-/dev.improve.haniverse.routes.nav.app.learn._lib.audio";
import { useMemo, useRef } from "react";
import { isNonEmpty } from "@-/com.npmjs.ts-array-length";
import arrayLength from "@-/com.npmjs.extra-array__length";

const EMPTY_PINYIN_ENTRIES = [] as const;

export function useNewMorphemeLessonLayoutContextValue({
  activeIndex,
  grapheme,
  lesson,
  lessonPart,
  morphemeMnemonicsPanelMorphemeSlice,
  onlyPinyin,
  setActiveIndex,
  viewNavigationRef,
}: {
  grapheme: string;
  lesson: Level;
  /**
   * What accepting the card enrolls. Primitive and glyph-only lessons show no pronunciation
   * pages; a pronunciation lesson introduces only its readings.
   */
  lessonPart: GraphemeLessonPart;
  /** Narrowed by `MorphemeLessonLayoutSuspense`, the lesson card's gate. */
  morphemeMnemonicsPanelMorphemeSlice: ResolvedQuery<GraphemeMorphemeMnemonicsPanelMorphemeSlice | null>;
  /** Single-reading 2-P1 slot (干 gan4, then 干 gan1). */
  onlyPinyin?: string;
} & MorphemePanelsMnemonicsNavigationProps) {
  const slice = morphemeMnemonicsPanelMorphemeSlice.data;
  const pronunciationEntries = usePrevalentPinyin(slice.grapheme, slice.pinyinList);
  const isGlyphOnlyLesson = lessonPart === "primitive" || lessonPart === "meaning";
  const studyPinyinEntryList = isGlyphOnlyLesson ? EMPTY_PINYIN_ENTRIES : pronunciationEntries;
  const studyPinyinList = useMemo(
    () => studyPinyinEntryList.map((entry) => entry.toneNumbersPinyin),
    [studyPinyinEntryList],
  );

  const { playForPinyin, playSequence } = useLearnMnemonicsProgrammaticAudio(slice);

  const hasAnyStudyPinyin = isNonEmpty(slice.pinyinList) && isNonEmpty(studyPinyinList);

  // The readings this lesson introduces — the same set enrollment uses on "Next Card". A
  // meaning-only lesson introduces none (Level 1 干 teaches the gloss alone); Level 1-P1's
  // pronunciation lessons introduce them as separate cards (干 gan4, then 干 gan1).
  const { hanziScript } = useActorUser();
  const curriculumGrade = lesson.curriculumGrade ?? 1;
  const introducedPinyinList = useMemo(
    () =>
      orderIntroducedPinyinForGrapheme(
        grapheme,
        studyPinyinEntryList
          .filter((reading) =>
            isGraphemeReadingIntroducedAtGrade({
              curriculumGrade,
              grapheme,
              onlyPinyin,
              part: lessonPart,
              pinyin: reading.toneNumbersPinyin,
              reading,
              script: hanziScript,
            }),
          )
          .map((reading) => reading.toneNumbersPinyin),
      ),
    [curriculumGrade, grapheme, hanziScript, lessonPart, onlyPinyin, studyPinyinEntryList],
  );
  // Mnemonics pages are `[meaning, ...studyPinyinList]`, so reading `i` is page `i + 1`. Follow
  // `introducedPinyinList` order (干 gan4 before gan1) rather than binder-tab order.
  const introducedPageIndexList = useMemo(
    () =>
      introducedPinyinList.flatMap((pinyin) => {
        const index = studyPinyinList.indexOf(pinyin);
        return index === -1 ? [] : [index + 1];
      }),
    [introducedPinyinList, studyPinyinList],
  );

  // The pages the learner has opened on this card, however they got there — the "Next
  // Pronunciation" button or a press on the binder tab's pinyin. This must update DURING render (a
  // mutable ref folded inline), not via a `useEffect` on `activeIndex`. With an effect the footer
  // needed TWO render passes to swap from "Next Pronunciation" to "Next Card" — render with the new
  // activeIndex, then the effect records it, then a SECOND render reflects it. Under load (Maestro
  // polling a deep view tree) that deferred effect was starved, so the footer intermittently never
  // swapped and the lesson card could not be completed. Recording inline makes
  // `hasUnviewedPronunciation` reflect the current `activeIndex` in the same render the index
  // changes. (Opening a page stays recorded, so going BACK to an earlier page still counts it.)
  //
  // Reset the record when the lesson grapheme changes — this hook lives in the persistent lesson
  // route layout, so its state survives moving from one card to the next. The shared `activeIndex`
  // (bumped by the mnemonics tab bar's effect) still holds the PREVIOUS card's last page index when
  // the next card's footer first renders, before that effect resets it to 0. Recording that stale
  // index would mark a page of the new card as viewed that the learner never saw, so on the render
  // where the grapheme just changed nothing is recorded; later renders record the new card's real
  // `activeIndex`.
  const lessonIdentity = `${slice.grapheme}\0${lessonPart}\0${onlyPinyin ?? ""}`;
  const viewedPageIndexSetRef = useRef(new Set<number>());
  const viewedLessonRef = useRef(lessonIdentity);
  const isNewLesson = viewedLessonRef.current !== lessonIdentity;
  if (isNewLesson) {
    viewedLessonRef.current = lessonIdentity;
    viewedPageIndexSetRef.current = new Set();
  } else {
    viewedPageIndexSetRef.current.add(isGlyphOnlyLesson ? 0 : activeIndex);
  }
  // The introduced reading the "Next Pronunciation" button opens next: the first one, in page
  // order, the learner has not opened yet. `undefined` once every introduced reading was viewed,
  // which turns the button into "Next Card".
  const nextUnviewedPronunciationPageIndex = introducedPageIndexList.find(
    (pageIndex) => !viewedPageIndexSetRef.current.has(pageIndex),
  );
  const hasUnviewedPronunciation = nextUnviewedPronunciationPageIndex !== undefined;

  // Whether the mnemonics pager still has a page to the RIGHT of the one on screen — i.e. whether a
  // leftward drag would be consumed by the inner pronunciation pager. The footer's swipe-to-advance
  // suppresses itself while this is true so it never steals that pager's gesture.
  //
  // Deliberately NOT `hasUnviewedPronunciation`: that one reads the sticky record of opened pages,
  // so after paging right and swiping back left it reports false while a page to the right plainly
  // still exists. This is a live question about the CURRENT index, so it reads
  // `activeIndex` directly. Pages are `[meaning, ...studyPinyinList]`, so the last valid index is
  // `studyPinyinList.length`.
  //
  // `isNewLesson` guards the same staleness the viewed-page record above documents: on the render
  // where the card just changed, `activeIndex` still holds the previous card's pinyin index, which
  // would claim a page to the right on a card that may have none. Treat that one render as "no page
  // to the right" so the swipe stays available rather than being wrongly suppressed.
  const hasNextPronunciationPage = !isNewLesson && activeIndex < arrayLength(studyPinyinList);

  // The cards of the level this lesson belongs to: a pronunciation lesson counts through its
  // pronunciation level (`Level 2-P1`), not its character level.
  const lessonCharacters =
    lessonPart === "pronunciation"
      ? (lesson.pronunciationCharacters ?? lesson.characters)
      : lesson.characters;

  return {
    activeIndex: isGlyphOnlyLesson ? 0 : activeIndex,
    grapheme,
    hasAnyStudyPinyin,
    hasNextPronunciationPage,
    hasUnviewedPronunciation,
    introducedPinyinList,
    lesson,
    lessonCharacters,
    lessonPart,
    morphemeMnemonicsPanelMorphemeSlice,
    nextUnviewedPronunciationPageIndex,
    onlyPinyin,
    playForPinyin,
    playSequence,
    setActiveIndex,
    studyPinyinList,
    viewNavigationRef,
  };
}

export type NewMorphemeLessonLayoutContextValue = ReturnType<
  typeof useNewMorphemeLessonLayoutContextValue
>;
